import styled from "styled-components";

/* Breakpoints & MQ helpers */
const bp = {
  phone: 0,
  tablet: 768,
  laptop: 1024,
  desktop: 1200,
};

const mq = {
  tabletOnly: `@media (min-width: ${bp.tablet}px) and (max-width: ${
    bp.desktop - 1
  }px)`,
  upTablet: `@media (min-width: ${bp.tablet}px)`,
  upLaptop: `@media (min-width: ${bp.laptop}px)`,
  upDesktop: `@media (min-width: ${bp.desktop}px)`,
};

/* Base Wrappers */
export const Shell = styled.div<{ $overflowY?: "scroll" | "hidden" }>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100svh;
  width: 100vw;
  overflow-y: ${({ $overflowY }) => $overflowY || "hidden"};
  overflow-x: hidden;
  background: #08142f;
  color: #ffffff;

  /* 平板與桌機：預設讓頁面可以直向滾動（可被 props 覆寫） */
  ${mq.upTablet} {
    overflow-y: ${({ $overflowY }) => $overflowY || "scroll"};
  }
`;

/* Fixed Background */
export const BackgroundFixed = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: #08142f;
  overflow: hidden;
`;

export const BgCover = styled.div<{ $offsetY?: number }>`
  position: absolute;
  inset: 0;
  background-image: url("/images/background-phone.png");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 50% 0;
  transform: translateY(${({ $offsetY = 0 }) => `${$offsetY}px`});
  will-change: transform;

  /* 平板：較寬的背景圖 */
  ${mq.tabletOnly} {
    background-image: url("/images/background-tablet.png");
    background-position: center top;
  }

  /* 桌機：桌機版背景圖 */
  ${mq.upDesktop} {
    background-image: url("/images/background-tablet.png"); // 使用同 tablet 版圖
    background-position: center top;
  }
`;

export const BgVignette = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    rgba(8, 20, 47, 0) 0%,
    rgba(8, 20, 47, 0) 55%,
    rgba(8, 20, 47, 0.35) 75%,
    rgba(8, 20, 47, 0.7) 90%,
    rgba(8, 20, 47, 1) 100%
  );
`;

/* Dust overlay */
export const DustOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12;
  pointer-events: none;
  background-image: url("/images/dust-overlay.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  mix-blend-mode: soft-light;
  opacity: 0.38;
`;

/* Header */
export const HeaderWrap = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 30;
  padding: 12px 16px;
  padding-top: calc(env(safe-area-inset-top, 0px) + 12px);

  ${mq.tabletOnly} {
    padding: 14px 20px;
    padding-top: calc(env(safe-area-inset-top, 0px) + 14px);
  }
  ${mq.upDesktop} {
    padding: 16px 24px;
    padding-top: calc(env(safe-area-inset-top, 0px) + 16px);
  }
`;

export const HeaderPill = styled.div`
  margin: 0 auto;
  max-width: 1024px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 34.5px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: linear-gradient(
      4.02deg,
      rgba(255, 255, 255, 0) 25.15%,
      rgba(255, 255, 255, 0.15) 75.26%
    ),
    radial-gradient(
      82.73% 82.73% at 50% 50%,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 100%
    ),
    linear-gradient(
      2.46deg,
      rgba(235, 241, 255, 0.2) 2.33%,
      rgba(0, 5, 164, 0.2) 170.47%
    );
  box-shadow: inset 0 1px 1.5px rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(7.4px);

  ${mq.tabletOnly} {
    max-width: 1180px;
    padding: 10px 16px;
    border-radius: 40px;
  }
  ${mq.upDesktop} {
    max-width: 1320px;
    padding: 12px 18px;
    border-radius: 42px;
  }
`;

export const BrandLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  line-height: 13px;
  cursor: pointer;

  img.brand {
    width: 100%;
    height: auto;
    margin-left: 8px;
    margin-top: -2px;
  }
  span.pipe {
    opacity: 0.4;
  }
  span.title {
    opacity: 0.9;
  }

  ${mq.tabletOnly} {
    font-size: 20px;
    gap: 10px;
    img.brand {
      margin-left: 10px;
    }
  }
  ${mq.upDesktop} {
    font-size: 21px;
    gap: 12px;
  }
`;

/* Lang Switch */
export const LangWrap = styled.div`
  position: relative;
  display: inline-flex;
`;
export const LangButton = styled.button.attrs({ type: "button" })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(20, 34, 78, 0.6);
  color: #e5ecff;
  border-radius: 9999px;
  padding: 8px 10px;
  gap: 8px;
  cursor: pointer;
  outline: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  &:hover {
    background: rgba(34, 58, 120, 0.7);
  }
  &:active {
    transform: translateY(1px);
  }
  &::after {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  }
  svg {
    width: 18px;
    height: 18px;
    opacity: 0.95;
  }

  ${mq.tabletOnly} {
    padding: 10px 12px;
    svg {
      width: 20px;
      height: 20px;
    }
  }
  ${mq.upDesktop} {
    padding: 10px 14px;
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const LangMenu = styled.ul`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 180px;
  padding: 6px;
  margin: 0;
  list-style: none;
  border-radius: 12px;
  background: linear-gradient(180deg, #0d1f4a 0%, #0b1a3d 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
  z-index: 30;

  ${mq.tabletOnly} {
    min-width: 200px;
    padding: 8px;
  }
  ${mq.upDesktop} {
    min-width: 220px;
  }
`;

export const LangItem = styled.li<{ $active?: boolean }>`
  button {
    width: 100%;
    text-align: left;
    border: none;
    background: ${({ $active }) =>
      $active ? "rgba(96,165,250,0.18)" : "transparent"};
    color: #fff;
    font-size: 14px;
    line-height: 1;
    padding: 12px 14px;
    border-radius: 10px;
    cursor: pointer;
    &:hover,
    &:focus {
      background: rgba(96, 165, 250, 0.28);
    }

    ${mq.tabletOnly} {
      font-size: 15px;
      padding: 12px 16px;
      border-radius: 12px;
    }
    ${mq.upDesktop} {
      font-size: 15.5px;
      padding: 12px 18px;
    }
  }
`;

export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/* =========
   Content
   ========= */
export const ContentWrap = styled.div<{ $padBottom?: number }>`
  position: relative;
  z-index: 10;
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-bottom: ${({ $padBottom = 0 }) => `${$padBottom}px`};
  padding-top: calc(env(safe-area-inset-top, 0px) + 78px);

  ${mq.tabletOnly} {
    padding-top: calc(env(safe-area-inset-top, 0px) + 88px);
  }
  ${mq.upDesktop} {
    padding-top: calc(env(safe-area-inset-top, 0px) + 96px);
  }
`;

export const ContentInner = styled.div<{
  $tabletMax?: number;
  $desktopMax?: number;
}>`
  flex: 1;
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;

  ${mq.tabletOnly} {
    max-width: ${({ $tabletMax }) => `${$tabletMax ?? 1080}px`};
    padding: 0 24px;
  }
  ${mq.upDesktop} {
    max-width: ${({ $desktopMax }) => `${$desktopMax ?? 1240}px`};
    padding: 0 28px;
  }
`;

/* =======
   Footer
   ======= */
export const FooterWrap = styled.div`
  position: relative;
  z-index: 15;
  width: 100%;
  height: 38px;

  ${mq.tabletOnly} {
    height: 48px;
  }
  ${mq.upDesktop} {
    height: 56px;
  }
`;

export const FooterOverlay = styled.div<{ $fixed?: boolean; $height?: number }>`
  position: ${({ $fixed }) => ($fixed ? "fixed" : "relative")};
  left: 0;
  right: 0;
  bottom: ${({ $fixed }) =>
    $fixed ? `calc(env(safe-area-inset-bottom, 0px))` : "0"};
  height: ${({ $height = 38 }) => `${$height}px`};
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.78) 0%,
    rgba(0, 0, 0, 0) 101.02%
  );
  backdrop-filter: blur(5.15px);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  ${mq.tabletOnly} {
    height: ${({ $height = 48 }) => `${Math.max($height, 48)}px`};
    backdrop-filter: blur(6px);
  }
  ${mq.upDesktop} {
    height: ${({ $height = 56 }) => `${Math.max($height, 56)}px`};
    backdrop-filter: blur(7px);
  }
`;

export const FooterSpacer = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => `${$height}px`};

  ${mq.tabletOnly} {
    height: ${({ $height }) => `${Math.max($height, 48)}px`};
  }
  ${mq.upDesktop} {
    height: ${({ $height }) => `${Math.max($height, 56)}px`};
  }
`;

export const PoweredText = styled.span`
  font-size: 1rem;
  letter-spacing: 0.4px;
  color: rgba(255, 255, 255, 0.85);
  text-transform: lowercase;
  margin-right: 8px;

  ${mq.tabletOnly} {
    font-size: 1.05rem;
  }
  ${mq.upDesktop} {
    font-size: 1.1rem;
  }
`;

export const WhoscallLogo = styled.img`
  height: 1.5rem;
  width: auto;
  display: block;
  ${mq.tabletOnly} {
    height: 1.65rem;
  }
  ${mq.upDesktop} {
    height: 1.8rem;
  }
`;
